// Centralized localized route mapping and helpers
// EN slugs (root) and FR slugs (with /fr prefix, ASCII only)

export const ROUTES = {
  home:   { en: '/',                        fr: '/fr' },
  experience:     { en: '/wedding-photographer',       fr: '/fr/photographe-mariage' },
  videographer:   { en: '/wedding-videographer',       fr: '/fr/videaste-mariage' },
  about:          { en: '/about',                      fr: '/fr/a-propos' },
  portfolio:      { en: '/portfolio',                  fr: '/fr/portfolio' },
  contact:        { en: '/contact',                    fr: '/fr/contact' },
  // Gallery route definitions (with :slug param); use helpers for concrete URLs
  gallery:        { en: '/portfolio/:slug',            fr: '/fr/portfolio/:slug' },
};

const STATIC_PATH_TO_KEY = (() => {
  const map = new Map();
  for (const [key, obj] of Object.entries(ROUTES)) {
    // skip gallery templates containing ':'
    if (obj.en && !obj.en.includes(':')) map.set(obj.en, key);
    if (obj.fr && !obj.fr.includes(':')) map.set(obj.fr, key);
  }
  return map;
})();

export function getPath(key, lang = 'en', params = {}) {
  const loc = ROUTES[key];
  if (!loc) return '/';
  const base = (lang === 'fr') ? loc.fr : loc.en;
  if (!base) return '/';
  // If it has params like :slug, interpolate
  return base.replace(/:([a-zA-Z0-9_]+)/g, (_, p1) => {
    return Object.prototype.hasOwnProperty.call(params, p1) ? `${params[p1]}` : `:${p1}`;
  });
}

function isFr(pathname) {
  return pathname.startsWith('/fr');
}

function stripFr(pathname) {
  return pathname.replace(/^\/fr/, '') || '/';
}

function ensureFrPrefix(path) {
  return path === '/' ? '/fr' : (path.startsWith('/fr') ? path : `/fr${path}`);
}

function removeFrPrefix(path) {
  return path.replace(/^\/fr/, '') || '/';
}

function isGalleryPath(path) {
  return path.startsWith('/portfolio/') || path.startsWith('/fr/portfolio/');
}

export function toLocalePath(pathname, lang = 'en') {
  // Galleries: just toggle /fr prefix, keep slug identical
  if (isGalleryPath(pathname)) {
    const stripped = pathname.replace(/^\/fr\//, ''); // remove fr/ if present
    // Ensure exactly one leading slash (avoid creating '//portfolio/...')
    const normalized = stripped.startsWith('/') ? stripped : `/${stripped}`;
    return lang === 'fr' ? ensureFrPrefix(normalized) : removeFrPrefix(normalized);
  }
  // Static pages
  const key = STATIC_PATH_TO_KEY.get(pathname);
  if (key) return getPath(key, lang);

  // Home
  if (pathname === '/' || pathname === '/fr') return getPath('home', lang);

  // Fallback: toggle /fr only
  return lang === 'fr' ? ensureFrPrefix(stripFr(pathname)) : removeFrPrefix(pathname);
}

export function mirrorPath(pathname, targetLang) {
  const lang = targetLang === 'fr' ? 'fr' : 'en';
  return toLocalePath(pathname, lang);
}

// Convenience helpers for components
export function pathFor(key, lang = 'en', params) {
  return getPath(key, lang, params);
}
